'use client';

import useSWR from 'swr';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { BASE_PATH } from '@/app/components/search/utils/path';
import { fetchWithTracingAndLogging } from '@/app/components/hotel-detail/common/fetchWithTracing';
import { getTranslatedCategories } from '@/app/components/hotel-detail/common/staticHotelDataAdapter.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';

/**
 * Lazily fetches a hotel's opinion data (aggregate rating + category breakdown) for the
 * TripAdvisor ratings popover on search cards. Same lazy-on-open hop the TrustYou popover
 * used (ranking → TY meta_review), but against our own hotel static endpoint, whose
 * `hotelOpinion.categories` is the field Supply repopulates with TA categories on cutover.
 */
export const useFetchHotelOpinion = (hotelId: number | undefined) => {
  const { localization, searchInfoData, commonData } = usePageWrapperData();
  const { t } = useTranslation();

  const loggingInfo: LoggingInfo = {
    tags: {
      page: searchInfoData.searchMode.toLowerCase(),
      service: 'hotel-static',
      module: 'useFetchHotelOpinion',
      debug: false,
    },
  };

  const key = hotelId
    ? `${BASE_PATH}/api/hotel-detail/static?id=${hotelId}&businessProfileId=${localization.businessProfile}&searchMode=${searchInfoData.searchMode}`
    : null;

  const { data, isLoading, error } = useSWR<HotelDetailSuccess>(
    key,
    (url: string) =>
      fetchWithTracingAndLogging<HotelDetailSuccess>(
        url,
        { method: 'GET', headers: { 'content-type': 'application/json' } },
        loggingInfo,
        searchInfoData.searchTraceParent,
        commonData
      ),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
      dedupingInterval: 1000 * 60 * 30,
    }
  );

  return {
    categories: getTranslatedCategories(data?.hotelOpinion?.categories, t),
    isLoading,
    isError: !!error,
  };
};
